import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  fetchClientsByStatus,
  approveClient,
  rejectClient
} from '../../../services/admin/api';
import './client-list.css';

const PendingClients = () => {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [sortColumn, setSortColumn] = useState('username');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [processing, setProcessing] = useState(false);
  const [actionState, setActionState] = useState({ id: null, type: null });

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    id: null,
    type: null,
  });

  const loadClients = async () => {
    try {
      setLoading(true);
      const res = await fetchClientsByStatus("PENDING");

      if (Array.isArray(res)) {
        setClients(res);
      } else {
        setClients([]);
      }
    } catch (err) {
      console.error("Error loading pending clients:", err);
      toast.error("Failed to load pending clients");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const filteredClients = useMemo(() => {
    return clients
      .filter((client) =>
        client.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.phoneNumber?.toString().includes(searchTerm)
      )
      .sort((a, b) => {
        const valA = a[sortColumn];
        const valB = b[sortColumn];

        if (valA == null) return 1;
        if (valB == null) return -1;

        if (typeof valA === "string" && typeof valB === "string") {
          return sortOrder === "asc"
            ? valA.toLowerCase().localeCompare(valB.toLowerCase())
            : valB.toLowerCase().localeCompare(valA.toLowerCase());
        }

        return sortOrder === "asc" ? valA - valB : valB - valA;
      });
  }, [clients, searchTerm, sortColumn, sortOrder]);

  const paginatedClients = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return filteredClients.slice(start, start + itemsPerPage);
  }, [filteredClients, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  const handleApprove = async (id) => {
    try {
      setProcessing(true);
      setActionState({ id, type: "approve" });

      await approveClient(id);
      toast.success("Client approved");
      loadClients();
    } catch {
      toast.error("Failed to approve client");
    } finally {
      setProcessing(false);
      setActionState({ id: null, type: null });
    }
  };

  const handleReject = async (id) => {
    try {
      setProcessing(true);
      setActionState({ id, type: "reject" });

      await rejectClient(id);
      toast.success("Client rejected");
      loadClients();
    } catch {
      toast.error("Failed to reject client");
    } finally {
      setProcessing(false);
      setActionState({ id: null, type: null });
    }
  };

  return (
    <div className="client-wrapper">
      <div className="clinet-table-controls">
        <div className="entries-select">
          Show{" "}
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries
        </div>

        <div className="client-search-box">
          <div className="client-search-input-wrapper">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
            />
            <Search className="client-search-icon" size={18} />
          </div>
        </div>
      </div>

      <div className="clinet-table-container">
        <table className="candidates-clinet-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('username')}>
                Client Name {sortColumn === 'username' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
              </th>
              <th onClick={() => handleSort('companyName')}>
                Company {sortColumn === 'companyName' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
              </th>
              <th onClick={() => handleSort('email')}>
                Email {sortColumn === 'email' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
              </th>
              <th onClick={() => handleSort('phoneNumber')}>
                Contact Number {sortColumn === 'phoneNumber' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
              </th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="no-data loading-text">
                  Loading Pending Clients...
                </td>
              </tr>
            ) : paginatedClients.length > 0 ? (
              paginatedClients.map((client) => (
                <tr key={client.id}>
                  <td>{client.username}</td>
                  <td>{client.companyName}</td>
                  <td>{client.email}</td>
                  <td>{client.phoneNumber}</td>
                  <td style={{ display: "flex", gap: "8px" }}>
                    <button
                      className="candidate-btn"
                      disabled={processing}
                      onClick={() =>
                        setConfirmModal({ open: true, id: client.id, type: "approve" })
                      }
                    >
                      {actionState.id === client.id && actionState.type === "approve"
                        ? "Approving..."
                        : "Approve"}
                    </button>

                    <button
                      className="candidate-btn"
                      style={{ backgroundColor: "#8b0000" }}
                      disabled={processing}
                      onClick={() =>
                        setConfirmModal({ open: true, id: client.id, type: "reject" })
                      }
                    >
                      {actionState.id === client.id && actionState.type === "reject"
                        ? "Rejecting..."
                        : "Reject"}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">
                  No pending clients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-summary">
          Showing {filteredClients.length === 0 ? 0 : currentPage * itemsPerPage + 1} to{" "}
          {Math.min((currentPage + 1) * itemsPerPage, filteredClients.length)} of{" "}
          {filteredClients.length} entries
        </div>

        <div className="pagination-buttons-user">
          <button
            className="page-btn"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
            disabled={currentPage === 0}
          >
            ←
          </button>
          <button className="page-btn active">{currentPage + 1}</button>
          <button
            className="page-btn"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
            disabled={currentPage >= totalPages - 1 || totalPages === 0}
          >
            →
          </button>
        </div>
      </div>


      {confirmModal.open && (
        <div
          className="confirm-overlay"
          onClick={() => setConfirmModal({ open: false, id: null, type: null })}
        >
          <div
            className="confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="confirm-close-btn"
              onClick={() =>
                setConfirmModal({ open: false, id: null, type: null })
              }
            >
              ✕
            </button>

            <h3 className="confirm-title">
              {confirmModal.type === "approve"
                ? "Approve Client?"
                : "Reject Client?"}
            </h3>

            <p>
              Are you sure you want to{" "}
              <strong>{confirmModal.type}</strong> this client?
            </p>

            <div className="confirm-actions">
              <button
                className="confirm-btn yes"
                onClick={() => {
                  if (confirmModal.type === "approve") {
                    handleApprove(confirmModal.id);
                  } else {
                    handleReject(confirmModal.id);
                  }
                  setConfirmModal({ open: false, id: null, type: null });
                }}
              >
                Yes
              </button>

              <button
                className="confirm-btn no"
                onClick={() =>
                  setConfirmModal({ open: false, id: null, type: null })
                }
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PendingClients;
